import {
  ActivityLog,
  ActivityLogEventType,
  entityLabels,
  eventColors,
  eventLabels,
} from '../models/ActivityLog'

// Keyed by the log object itself: re-renders handing back the same reference hit the
// cache instead of recomputing the diff.
const differencesCache = new WeakMap<ActivityLog, any[]>()

export const extractDifferencesFromLog = (log: ActivityLog): any[] => {
  const cached = differencesCache.get(log)
  if (cached !== undefined) return cached

  const differences = computeDifferencesFromLog(log)
  differencesCache.set(log, differences)
  return differences
}

const computeDifferencesFromLog = (log: ActivityLog): any[] => {
  const differences: any[] = []
  const oldJson = log.properties.old
  const newJson = log.properties.attributes
  const lang = log.properties.attributes?.locale

  if (newJson === null) {
    return []
  }

  const compare = (oldObj: any, newObj: any, path: string = '') => {
    if (oldObj == null || newObj == null) {
      if (oldObj !== newObj) {
        differences.push({ key: path, oldValue: oldObj, newValue: newObj })
      }
      return
    }

    for (let key in oldObj) {
      const currentPath = path ? `${path}.${key}` : key

      if (!newObj.hasOwnProperty(key)) {
        differences.push({
          key: currentPath,
          oldValue: oldObj[key],
          newValue: null,
        })
      } else if (!Object.is(oldObj[key], newObj[key])) {
        if (
          typeof oldObj[key] === 'object' &&
          oldObj[key] !== null &&
          typeof newObj[key] === 'object' &&
          newObj[key] !== null
        ) {
          compare(oldObj[key], newObj[key], currentPath)
        } else {
          differences.push({
            key: currentPath,
            oldValue: oldObj[key],
            newValue: newObj[key],
          })
        }
      }
    }

    for (let key in newObj) {
      const currentPath = path ? `${path}.${key}` : key
      if (!oldObj.hasOwnProperty(key)) {
        differences.push({
          key: currentPath,
          oldValue: null,
          newValue: newObj[key],
        })
      }
    }
  }
  compare(oldJson, newJson)
  if (differences.length > 0 && lang) {
    differences.push({ key: 'language', language: lang })
  }

  return differences.reverse()
}

export const getLabelForEntity = (entity: string): string => {
  const label = entityLabels[entity] || entity
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export const formatLogMessage = (log: ActivityLog): string => {
  const label = getLabelForEntity(log.logName)
  const capitalize = (str?: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''
  const name =
    `${capitalize(log.causer?.first_name)} ${capitalize(log.causer?.last_name)}`.trim()
  const event = eventLabels[log.event] ?? log.event
  return `${label} ${event} by ${name || 'System'}`.trim()
}

export const getColorForEvent = (event: ActivityLogEventType): string =>
  eventColors[event] || 'orange'
const flattenObject = (obj: any, prefix = ''): { [key: string]: any } => {
  let result: { [key: string]: any } = {}

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key
      const value = obj[key]

      if (typeof value === 'object' && value !== null) {
        result = { ...result, ...flattenObject(value, newKey) }
      } else {
        result[newKey] = value
      }
    }
  }

  return result
}

export const formatActivityLogValue = (value: any): string => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return String(value ?? '')
  }

  // Arrays keep the plain comma-separated rendering they have always had.
  if (Array.isArray(value)) {
    return value.join(', ')
  }

  // A named entity (a document, a translation) reads better by its name than as
  // raw JSON. `?.` matters: a locale map can carry a null locale.
  if (value.name !== undefined) {
    return String(value.name)
  }

  if (value.en?.name !== undefined) {
    return String(value.en.name)
  }

  return JSON.stringify(value)
}

// Keyed by the input object: re-renders handing back the same `attributes` reference
// hit the cache instead of re-flattening.
const flattenedPropertiesCache = new WeakMap<object, Record<string, any>>()

export const getFlattenedProperties = (
  properties: any
): Record<string, any> => {
  if (properties === null || typeof properties !== 'object') {
    return Object.entries(flattenObject(properties))
  }

  const cached = flattenedPropertiesCache.get(properties)
  if (cached !== undefined) return cached

  const flattened = Object.entries(flattenObject(properties))
  flattenedPropertiesCache.set(properties, flattened)
  return flattened
}

export function formatKeyLabel(key?: string): string {
  if (!key || typeof key !== 'string') {
    return '' // Retourne une chaîne vide si la clé est invalide
  }

  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Sépare les mots camelCase
    .replace(/\./g, ' > ') // Transforme les "." en " > " pour les chemins
    .replace(/_/g, ' ') // Remplace les underscores par des espaces
    .replace(/\b\w/g, (char) => char.toUpperCase()) // Met en majuscule chaque mot
}
